/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * RemoveAttachedModal — every result attached to this race, and a way to take
 * any of them back off it.
 *
 * WHY IT REPLACES THE "PICKED THE WRONG RUN?" LIST. That list was built from
 * the LAST attach call's reply, so it held only what that one call wrote: a
 * row attached on its own replaced it, closing the dialog dropped it, and a
 * reload lost it for good. An organizer who attached five hundred people and
 * spotted one wrong run an hour later had nothing to undo from. This dialog
 * reads the RACE instead — every attached result, however and whenever it got
 * there — so there is nothing to remember and nothing to lose.
 *
 * THE SAME CHROME as the attach dialog and the daily one: DailyAttachModal's
 * stylesheet, its dam-* classes, its table, pager and footer. Search and paging
 * narrow only what is DRAWN; they never change what gets removed.
 *
 * NOTHING IS TICKED WHEN IT OPENS, and that is the one deliberate difference
 * from attaching. Attach starts with everyone ticked because the cost of a
 * wrong tick is a result you can remove. Here the cost is the result itself, so
 * the organizer ticks what they mean, and the button asks a second time.
 *
 * AN AWARD BLOCKS REMOVAL, published or not. This is the guard from
 * PublishResultsPage, repeated here because it is the only thing stopping it:
 * event_result is SOFT deleted, so the foreign key is never tested and the
 * award is left pointing at a row the board can no longer read — the podium
 * then shows that winner as "Time not available" for ever. Deleting the award
 * instead is not the way out either; the award table's uniqueness still counts
 * deleted rows, so the placing could never be awarded again. The way to remove
 * a declared winner is: clear their place, publish again, then remove.
 *
 * A MULTI-DAY EVENT (`multiDay`) lists each entrant once, with every run
 * attached to them added up — the organizer results page's totals query. Its
 * entry rows carry no time of their own (the runs are splits under them), so
 * the race read would show every one as blank. Removing an entrant takes the
 * entry and, with it, every run attached to them on any day: the attach
 * queries only read splits under a live entry, so all their runs are free to
 * attach again. The same thing the organizer results page's Remove does.
 *
 * AND IT IS PAGED ON THE SERVER, not read whole. That query sits behind the
 * analytics throttle (5/sec, 60/min) and answers 100 rows a call, so reading a
 * 20,000-entrant field up front was 200 calls — the dialog sat on "Reading…"
 * until the throttle answered 429. Here each page and each search is one call,
 * ticks are kept (with their rows) across page turns, and the header box ticks
 * the PAGE in view rather than the whole field.
 *
 * TICKING THE WHOLE FIELD is the "Tick all 20,000" link that appears once a
 * page is ticked. It is held as "everyone, except these", so nothing is read
 * to tick it; unticking a row adds to the exceptions. Only Remove reads the
 * field's ids — through the public table read, 2,000 a call (about ten calls
 * for 20,000), not the throttled totals query's 100 — and then removes them
 * several at a time (removeResults). Podium results are left out as always.
 *
 * idOf, NEVER String(): the fetch API hands event_result_id back expanded as an
 * object, and String() on it yields "[object Object]" — a set that matches
 * nothing and offers a Remove on every published winner.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAllRaceResults } from './boardApi';
import {
  fetchAwards, fetchOrganizerResultsPage, removeResults, ORGANIZER_RESULTS_PAGE_SIZE,
} from './publishApi';
import { idOf } from './boardIndexModel';
import { formatClock, formatKm, formatPace } from './boardModel';
import { CSS, pageWindow, initialsOf, startedAt } from './DailyAttachModal';
import ChronoBusy from './ChronoBusy';

const MUTED = '#6B5844';
const HAIR = 'rgba(36, 23, 8, .10)';
const DANGER = '#B91C1C';

const PER_PAGE = 100;
const SEARCH_DELAY_MS = 350;
const REFUSED_SHOWN = 20;
const numberFmt = new Intl.NumberFormat('en-IN');

const EXTRA_CSS = `
.ram-find{min-height:42px;border-radius:12px;border:1px solid ${HAIR};padding:0 12px;font-size:14px;
  font-weight:600;color:#241708;background:#fff;min-width:210px}
.ram-find::placeholder{color:#A69683;font-weight:500}
.ram-btn-danger{background:${DANGER};border:0;color:#fff;box-shadow:0 6px 16px rgba(185,28,28,.28)}
/* normal, NOT nowrap: the shared sheet clips an overflowing cell with an
   ellipsis, so a one-line note was cut to "On the podium — clear the pla…".
   It is a sentence, so let it wrap inside the column. */
.ram-locked{font-size:12px;color:${MUTED};white-space:normal;line-height:1.4}
.dam-table tr.ram-blocked td{background:#FBF8F4}
.ram-everyone{margin:12px 0 0;padding:10px 14px;border-radius:12px;background:#FFF4EC;font-size:14px;color:#4A3D33}
.ram-everyone.on{background:#FDECEC;color:#7F1D1D}
@media (max-width:720px){.ram-find{min-width:0;width:100%}}
`;

/** "3 activities · 2 days" — what a multi-day entrant has attached. */
export function attachedCaption(row) {
  const runs = Number(row.activities) || 0;
  const days = Number(row.days_active) || 0;
  return `${runs} ${runs === 1 ? 'activity' : 'activities'} · ${days} ${days === 1 ? 'day' : 'days'}`;
}

export default function RemoveAttachedModal({
  api, eventActivityId, multiDay = false, onClose,
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  // The search the server is asked for, once typing settles (multi-day only).
  const [term, setTerm] = useState('');
  // Ticked rows, result id → row, so a tick on a page no longer drawn still
  // knows who it is. Starts EMPTY — see the file header.
  const [ticked, setTicked] = useState(() => new Map());
  // The whole field ticked (multi-day): everyone but `excluded`.
  const [everyone, setEveryone] = useState(false);
  const [excluded, setExcluded] = useState(() => new Set());
  // The field's size with no search applied — what "everyone" means.
  const [fieldTotal, setFieldTotal] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [report, setReport] = useState(null);
  // Reading the whole field's ids before a remove-everyone starts.
  const [reading, setReading] = useState(false);

  useEffect(() => {
    if (!multiDay) return undefined;
    const next = search.trim();
    if (next === term) return undefined;
    const id = setTimeout(() => { setTerm(next); setPage(1); }, SEARCH_DELAY_MS);
    return () => clearTimeout(id);
  }, [multiDay, search, term]);

  const results = useQuery({
    queryKey: multiDay
      ? ['board', 'race-totals-page', eventActivityId, term, page]
      : ['board', 'race-results-all', eventActivityId],
    queryFn: ({ signal }) => (multiDay
      ? fetchOrganizerResultsPage(api, {
        eventActivityId, page, search: term, multiDay: true, signal,
      })
      : fetchAllRaceResults(api, { eventActivityId, signal })),
    enabled: Number.isFinite(eventActivityId),
    // A page turn keeps the last page up while the next one comes.
    placeholderData: multiDay ? keepPreviousData : undefined,
  });

  const awards = useQuery({
    queryKey: ['organizer', 'awards', eventActivityId],
    queryFn: ({ signal }) => fetchAwards(api, { eventActivityId, signal }),
    enabled: Number.isFinite(eventActivityId),
  });

  /** Results on the podium. Remove is refused for these — the only guard. */
  const awardedIds = useMemo(
    () => new Set(
      ((awards.data && awards.data.rows) || [])
        .map((a) => idOf(a.event_result_id))
        .filter((id) => id !== null && id !== undefined)
        .map(String),
    ),
    [awards.data],
  );

  const rows = useMemo(() => ((results.data && results.data.rows) || []), [results.data]);
  useEffect(() => {
    if (multiDay && !term && results.data && !results.isPlaceholderData) {
      setFieldTotal(Number(results.data.total) || 0);
    }
  }, [multiDay, term, results.data, results.isPlaceholderData]);
  const removable = useMemo(
    () => rows.filter((r) => !awardedIds.has(String(r.id))),
    [rows, awardedIds],
  );

  useEffect(() => {
    const before = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (dialogRef.current) dialogRef.current.focus();
    return () => {
      document.body.style.overflow = overflow;
      if (before && before.focus) before.focus();
    };
  }, []);
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && !busy) onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const nameOfRow = (row) => row.athlete_name || `Result ${row.id}`;

  /** The search narrows what is DRAWN. It never narrows what is removed. */
  const matching = useMemo(() => {
    const needle = search.trim().toLowerCase();
    // Multi-day: the server already searched and paged it.
    if (multiDay || !needle) return rows;
    return rows.filter((row) => (
      nameOfRow(row).toLowerCase().includes(needle)
      || String(row.bib_number || '').toLowerCase().includes(needle)
    ));
  }, [multiDay, rows, search]);

  // How many there are to page through: the server's total on a multi-day
  // event (it only ever sent one page), the list itself on a race.
  const matchingCount = multiDay ? Number(results.data && results.data.total) || 0 : matching.length;
  const perPage = multiDay ? ORGANIZER_RESULTS_PAGE_SIZE : PER_PAGE;
  const pages = Math.max(1, Math.ceil(matchingCount / perPage));
  const current = Math.min(page, pages);
  const shown = useMemo(
    () => (multiDay ? matching : matching.slice((current - 1) * PER_PAGE, current * PER_PAGE)),
    [multiDay, matching, current],
  );

  // What the header box ticks: every removable result on a race; on a
  // multi-day event, the removable rows of the page in view.
  const pool = multiDay ? shown.filter((r) => !awardedIds.has(String(r.id))) : removable;
  const isOn = (row) => (everyone
    ? !excluded.has(String(row.id)) && !awardedIds.has(String(row.id))
    : ticked.has(String(row.id)));
  const fieldRemovable = Math.max(0, fieldTotal - awardedIds.size);
  const selected = everyone ? Math.max(0, fieldRemovable - excluded.size) : ticked.size;
  const allTicked = pool.length > 0 && pool.every(isOn);
  // Offer the whole field once the page in view is all ticked and there is more.
  const offerEveryone = multiDay && !everyone && allTicked && fieldRemovable > pool.length;

  function clearTicks() {
    setTicked(new Map());
    setEveryone(false);
    setExcluded(new Set());
    setConfirming(false);
  }

  function tickEveryone() {
    setConfirming(false);
    setEveryone(true);
    setExcluded(new Set());
    setTicked(new Map());
  }

  function toggle(row) {
    if (awardedIds.has(String(row.id))) return;     // the guard
    setConfirming(false);
    if (everyone) {
      setExcluded((was) => {
        const next = new Set(was);
        const key = String(row.id);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
      });
      return;
    }
    setTicked((was) => {
      const next = new Map(was);
      const key = String(row.id);
      if (next.has(key)) next.delete(key); else next.set(key, row);
      return next;
    });
  }

  /** The header box: see `pool`. */
  function toggleAll() {
    setConfirming(false);
    if (everyone) {
      // Unticking the page in view takes it out of "everyone"; ticking it
      // again puts it back.
      setExcluded((was) => {
        const next = new Set(was);
        pool.forEach((r) => { if (allTicked) next.add(String(r.id)); else next.delete(String(r.id)); });
        return next;
      });
      return;
    }
    setTicked((was) => {
      const next = new Map(was);
      pool.forEach((r) => {
        if (allTicked) next.delete(String(r.id)); else next.set(String(r.id), r);
      });
      return next;
    });
  }

  async function onRemove() {
    if (!confirming) { setConfirming(true); return; }
    setConfirming(false);
    setBusy(true);
    setReport(null);

    let rowsToRemove;
    if (everyone) {
      // The whole field's ids, read now and only now — see the file header.
      setReading(true);
      try {
        const field = await fetchAllRaceResults(api, { eventActivityId });
        rowsToRemove = (field.rows || []).filter((r) => (
          !awardedIds.has(String(r.id)) && !excluded.has(String(r.id))
        ));
      } catch (e) {
        setReport({ removed: 0, refused: [{ name: 'This race', reason: (e && e.message) || 'Could not read the results to remove.' }] });
        setBusy(false);
        setReading(false);
        return;
      }
      setReading(false);
    } else {
      rowsToRemove = [...ticked.values()].filter((r) => !awardedIds.has(String(r.id)));
    }
    const picks = rowsToRemove.map((r) => ({ id: r.id, name: nameOfRow(r) }));
    if (!picks.length) { setBusy(false); return; }

    setProgress({ done: 0, total: picks.length });
    const { removed, refused } = await removeResults(api, picks, {
      onProgress: (done, total) => setProgress({ done, total }),
    });
    if (everyone) {
      // Whatever was refused is still there; tick it by hand to try again.
      setEveryone(false);
      setExcluded(new Set());
    }
    setTicked((was) => {
      const next = new Map(was);
      removed.forEach((id) => next.delete(id));
      return next;
    });
    setReport({ removed: removed.length, refused });
    setBusy(false);
    setProgress(null);
    // The board, its counts and the attach list all moved.
    queryClient.invalidateQueries({ queryKey: ['board'] });
    queryClient.invalidateQueries({ queryKey: ['organizer', 'awards', eventActivityId] });
  }

  const firstShown = shown.length ? (current - 1) * perPage + 1 : 0;
  const lastShown = shown.length ? firstShown + shown.length - 1 : 0;
  const pct = progress && progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
  const loading = results.isPending || awards.isPending;

  return createPortal((
    <div
      className="dam-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <style>{CSS}</style>
      <style>{EXTRA_CSS}</style>
      <div
        ref={dialogRef}
        className="dam-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ram-title"
        tabIndex={-1}
      >
        <header className="dam-head">
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div style={{ minWidth: 0 }}>
              <p style={{
                margin: 0, fontSize: 12, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: DANGER,
              }}>
                Picked the wrong run?
              </p>
              <h2 id="ram-title" style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 800 }}>Attached results</h2>
              <p style={{ margin: '4px 0 0', fontSize: 14, color: MUTED }}>
                {multiDay
                  ? 'Tick anyone attached by mistake. Removing takes every run attached to them, on every day, off the board — attach them again day by day.'
                  : 'Tick anyone attached by mistake. Removing puts them back in the attach list with every run they recorded.'}
              </p>
            </div>
            <button type="button" className="dam-x" aria-label="Close" onClick={onClose} disabled={busy}>×</button>
          </div>
          <div className="dam-stats">
            <input
              className="ram-find"
              type="search"
              aria-label="Find an athlete"
              placeholder="Find an athlete or bib"
              value={search}
              disabled={busy}
              onChange={(e) => { setSearch(e.target.value); if (!multiDay) setPage(1); }}
            />
            <span className="dam-chip" aria-label="Attached results"><b>{numberFmt.format(multiDay ? matchingCount : rows.length)}</b> {multiDay && term ? 'matching' : 'attached'}</span>
            {awardedIds.size > 0 && (
              <span className="dam-chip" title="On the podium — clear the place and publish again to remove">
                <b>{numberFmt.format(awardedIds.size)}</b> on the podium
              </span>
            )}
            <span className={`dam-chip${selected ? ' on' : ''}`}><b>{numberFmt.format(selected)}</b> ticked</span>
            {selected > 0 && !busy && (
              <button type="button" className="dam-link" onClick={clearTicks}>
                Clear
              </button>
            )}
          </div>
          {offerEveryone && (
            <p className="ram-everyone" role="status">
              {`All ${numberFmt.format(pool.length)} on this page are ticked.`}
              {' '}
              <button type="button" className="dam-link" onClick={tickEveryone} disabled={busy}>
                {`Tick all ${numberFmt.format(fieldRemovable)} attached`}
              </button>
            </p>
          )}
          {everyone && (
            <p className="ram-everyone on" role="status">
              {`All ${numberFmt.format(selected)} attached results are ticked`}
              {excluded.size > 0 ? `, except ${numberFmt.format(excluded.size)} you unticked` : ''}
              {awardedIds.size > 0 ? ` (podium results stay)` : ''}
              .
              {' '}
              <button type="button" className="dam-link" onClick={clearTicks} disabled={busy}>
                Clear selection
              </button>
            </p>
          )}
        </header>

        {progress && (
          <div className="dam-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <i style={{ width: `${pct}%` }} />
          </div>
        )}

        <div className="dam-body" aria-busy={results.isFetching}>
          {loading && <p className="dam-state">Reading this race&apos;s results…</p>}
          {results.isError && !results.data && (
            <div className="dam-state">
              <p style={{ margin: '0 0 12px', color: '#8A2C06' }}>{results.error.message}</p>
              <button type="button" className="dam-btn ghost" onClick={() => results.refetch()}>Try again</button>
            </div>
          )}
          {!loading && !shown.length && (
            <p className="dam-state">
              {(multiDay ? term : search.trim()) ? `Nobody matching “${multiDay ? term : search.trim()}”.` : 'No results are attached to this race yet.'}
            </p>
          )}
          {shown.length > 0 && (
            <table className="dam-table">
              <thead>
                <tr>
                  {/* dam-check, not a bare cell: the shared sheet gives every
                      td `overflow:hidden;text-overflow:ellipsis` and 16px of
                      side padding, which on a 44px column leaves 12px for an
                      18px checkbox — it overflowed and drew an ellipsis beside
                      every box. dam-check is the class their table uses for
                      exactly this column. */}
                  <th className="dam-check" style={{ width: 44 }}>
                    <input
                      type="checkbox"
                      aria-label={multiDay ? 'Tick every result on this page that can be removed' : 'Tick every result that can be removed'}
                      checked={allTicked}
                      ref={(el) => { if (el) el.indeterminate = selected > 0 && !allTicked; }}
                      onChange={toggleAll}
                      disabled={busy || pool.length === 0}
                    />
                  </th>
                  <th>Athlete</th>
                  <th>{multiDay ? 'Attached' : 'Started'}</th>
                  <th className="dam-num">{multiDay ? 'Total distance' : 'Distance'}</th>
                  <th className="dam-num">{multiDay ? 'Total time' : 'Time'}</th>
                  <th className="dam-num dam-hide-sm">Pace /km</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((row) => {
                  const key = String(row.id);
                  const blocked = awardedIds.has(key);
                  const on = isOn(row);
                  const name = nameOfRow(row);
                  return (
                    <tr
                      key={key}
                      className={`${on ? 'picked' : ''}${blocked ? ' ram-blocked' : ''}`}
                      onClick={() => { if (!busy) toggle(row); }}
                    >
                      <td className="dam-check">
                        {blocked ? (
                          <span title="On the podium — clear the place and publish again first" aria-hidden="true">🏅</span>
                        ) : (
                          <input
                            type="checkbox"
                            aria-label={`Remove the result for ${name}`}
                            checked={on}
                            disabled={busy}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggle(row)}
                          />
                        )}
                      </td>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                          <span className="dam-avatar" aria-hidden="true">{initialsOf(name)}</span>
                          <span style={{ fontWeight: 700 }}>{name}</span>
                        </span>
                        {blocked && (
                          <div className="ram-locked">On the podium — clear the place and publish again to remove</div>
                        )}
                      </td>
                      <td>{multiDay ? attachedCaption(row) : startedAt(row.started_at)}</td>
                      <td className="dam-num">{formatKm(row.distance_meters)}</td>
                      <td className="dam-num">{formatClock(row.duration_seconds)}</td>
                      <td className="dam-num dam-hide-sm">
                        {formatPace(row.duration_seconds, row.distance_meters)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {report && (
          <div role="status" style={{
            padding: '10px 24px', borderTop: `1px solid ${HAIR}`, fontSize: 14, maxHeight: 140, overflow: 'auto',
          }}>
            {report.removed > 0 && (
              <p style={{ margin: 0, color: '#1B7F48', fontWeight: 700 }}>
                {numberFmt.format(report.removed)} result{report.removed === 1 ? '' : 's'} removed. They are back in the attach list.
              </p>
            )}
            {/* Capped: a failed remove-everyone could list thousands. */}
            {report.refused.slice(0, REFUSED_SHOWN).map((r, i) => (
              <p key={`${r.name}-${i}`} style={{ margin: '4px 0 0', color: '#8A2C06' }}>
                {r.name}: {r.reason}
              </p>
            ))}
            {report.refused.length > REFUSED_SHOWN && (
              <p style={{ margin: '4px 0 0', color: '#8A2C06' }}>
                {`…and ${numberFmt.format(report.refused.length - REFUSED_SHOWN)} more could not be removed.`}
              </p>
            )}
          </div>
        )}

        <footer className="dam-foot">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 13, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
              {shown.length
                ? `Showing ${numberFmt.format(firstShown)}–${numberFmt.format(lastShown)} of ${numberFmt.format(matchingCount)}`
                : 'Nothing to show'}
            </span>
            {pages > 1 && (
              <nav className="dam-pager" aria-label="Pages">
                <button type="button" className="dam-pg" aria-label="Previous page" disabled={current <= 1} onClick={() => setPage(current - 1)}>‹</button>
                {pageWindow(current, pages).map((n, i) => (n === '…'
                  ? <span key={`gap-${i}`} style={{ padding: '0 4px', color: MUTED }}>…</span>
                  : (
                    <button
                      key={n}
                      type="button"
                      className={`dam-pg${n === current ? ' cur' : ''}`}
                      aria-current={n === current ? 'page' : undefined}
                      onClick={() => setPage(n)}
                    >
                      {numberFmt.format(n)}
                    </button>
                  )))}
                <button type="button" className="dam-pg" aria-label="Next page" disabled={current >= pages} onClick={() => setPage(current + 1)}>›</button>
              </nav>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginLeft: 'auto' }}>
            {/* Was: an inline "Removing x of y". Removing is one request per
                result, so a long field takes as long as attaching did — the
                same stopwatch, for the same reason. See ChronoBusy below. */}
            <button
              type="button"
              className="dam-btn ghost"
              onClick={() => (confirming ? setConfirming(false) : onClose())}
              disabled={busy}
            >
              {confirming ? 'Keep them' : 'Cancel'}
            </button>
            {/* NO onBlur DISARM HERE. It looks like a safety net and is the
                opposite: pressing "Keep them" blurs this button first, so the
                disarm ran before that button's own click handler read the
                armed state — and "Keep them" closed the dialog instead of
                standing the removal down. Measured in the UI suite, test K.
                The arm is cleared by "Keep them", by changing the selection
                (see toggle/toggleAll) and by closing; it never survives any of
                those, so nothing is left armed behind the organizer's back. */}
            <button
              type="button"
              className="dam-btn ram-btn-danger"
              onClick={onRemove}
              disabled={busy || selected === 0}
            >
              {/* eslint-disable-next-line no-nested-ternary */}
              {busy
                ? 'Removing…'
                : (confirming
                  ? `Press again to remove ${numberFmt.format(selected)}`
                  : `Remove selected (${numberFmt.format(selected)})`)}
            </button>
          </div>
        </footer>
      </div>

      <ChronoBusy
        open={busy}
        title={reading
          ? 'Reading every attached result on this race'
          : `Removing ${numberFmt.format(progress ? progress.total : selected)} ${(progress ? progress.total : selected) === 1 ? 'result' : 'results'} from this race`}
        note={reading
          ? 'Gathering the whole field before anything is removed. Please leave this page open.'
          : 'Each result is being taken off the race and its athlete put back in the attach list. Please leave this page open.'}
        progress={reading ? null : progress}
      />
    </div>
  ), document.body);
}
